<template>
  <section class="branch-home-intro">
    <article class="home-overview">
      <div class="home-overview__inner">
        <header class="home-overview__head">
          <h4>重要新闻</h4>
          <button type="button" class="text-link" @click="$emit('more')">
            更多 &gt;&gt;
          </button>
        </header>
        <div v-if="featuredList.length" class="home-notice-list">
          <button
            v-for="item in carouselList"
            :key="item.entryId || item.title"
            type="button"
            class="home-notice-item"
            @click="$emit('feature', item)"
          >
            <span>{{ item.title }}</span>
            <time>{{ item.date || '' }}</time>
          </button>
        </div>
        <p v-else class="home-overview__empty">暂无重要新闻</p>
      </div>
    </article>

    <div class="featured-notice">
      <template v-if="featuredList.length">
        <button
          type="button"
          class="featured-notice__card"
          :class="{ 'is-fallback': !activeCoverUrl }"
          @click="$emit('feature', activeFeature)"
        >
          <div class="featured-notice__media" aria-hidden="true">
            <img v-if="activeCoverUrl" :src="activeCoverUrl" :alt="activeFeature.title || ''">
          </div>
          <div class="featured-notice__caption">
            <span class="featured-notice__tag">{{ activeFeature.category || '新闻通知' }}</span>
            <strong>{{ activeFeature.title }}</strong>
            <time v-if="activeFeature.date">{{ activeFeature.date }}</time>
          </div>
        </button>
        <div v-if="carouselList.length > 1" class="featured-notice__nav" aria-label="新闻通知切换">
          <button
            v-for="(item, index) in carouselList"
            :key="item.entryId || index"
            type="button"
            :class="{ active: activeIndex === index }"
            :aria-label="`第 ${index + 1} 条`"
            @click.stop="activeIndex = index"
          />
        </div>
      </template>
      <div v-else class="featured-notice__empty">
        <p class="section-kicker">新闻通知</p>
        <h2>暂无轮播内容</h2>
      </div>
    </div>
  </section>
</template>

<script>
export default {
  name: 'BranchHomeIntro',
  props: {
    featuredList: { type: Array, default: () => [] },
    resolveUrl: { type: Function, default: url => url }
  },
  data() {
    return {
      activeIndex: 0,
      timer: null
    }
  },
  computed: {
    carouselList() {
      return this.featuredList.slice(0, 5)
    },
    activeFeature() {
      return this.carouselList[this.activeIndex] || this.carouselList[0] || null
    },
    activeCoverUrl() {
      if (!this.activeFeature) return ''
      const url = this.activeFeature.coverUrl || this.activeFeature.coverImageUrl || this.activeFeature.mediaUrl
      return url ? this.resolveUrl(url) : ''
    }
  },
  watch: {
    featuredList: {
      immediate: true,
      handler() {
        this.activeIndex = 0
        this.restart()
      }
    }
  },
  beforeDestroy() {
    this.clearTimer()
  },
  methods: {
    clearTimer() {
      if (this.timer) {
        clearInterval(this.timer)
        this.timer = null
      }
    },
    restart() {
      this.clearTimer()
      if (this.carouselList.length <= 1) return
      this.timer = setInterval(() => {
        this.activeIndex = (this.activeIndex + 1) % this.carouselList.length
      }, 5000)
    }
  }
}
</script>

<style scoped>
.branch-home-intro {
  display: flex;
  align-items: stretch;
  gap: 28px;
}

.home-overview {
  position: relative;
  flex: 1 1 0;
  min-width: 0;
  /* 列表高度与右侧轮播保持一致 */
}

.home-overview__inner {
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  box-sizing: border-box;
  border: 1px solid rgba(31, 79, 122, 0.12);
  border-radius: 10px;
  padding: 14px 18px 12px;
  background: #fff;
}

.home-overview__head {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 10px;
  flex: 0 0 auto;
  margin-bottom: 8px;
}

.home-overview__head h4 {
  margin: 0;
  color: var(--primary);
  font-size: 18px;
  font-weight: 800;
  letter-spacing: 0.02em;
}

.home-overview__head .text-link {
  border: 0;
  background: transparent;
  color: var(--subtle, #6b7280);
  font-size: 12px;
  cursor: pointer;
  padding: 0;
  white-space: nowrap;
}

.home-overview__head .text-link:hover {
  color: var(--primary);
  text-decoration: underline;
}

.home-notice-list {
  display: flex;
  flex-direction: column;
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
}

.home-notice-item {
  display: flex;
  flex-shrink: 0;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  min-height: 34px;
  border: 0;
  border-bottom: 1px dashed var(--border, #e5e1da);
  padding: 5px 0;
  background: transparent;
  color: var(--text);
  text-align: left;
  cursor: pointer;
}

.home-notice-item span {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.home-notice-item time {
  flex: 0 0 auto;
  color: var(--subtle);
  font-size: 12px;
}

.home-notice-item:hover span {
  color: var(--primary);
}

.home-overview__empty {
  margin: 0;
  color: var(--subtle);
  font-size: 14px;
  line-height: 1.7;
}

.featured-notice {
  position: relative;
  flex: 0 0 min(38%, 480px);
  width: min(38%, 480px);
  min-width: 260px;
  min-height: 0;
  aspect-ratio: 16 / 9;
}

.featured-notice__card {
  position: absolute;
  inset: 0;
  aspect-ratio: auto;
  height: auto;
}

.featured-notice__empty {
  min-height: 0;
  height: 100%;
}

@media (max-width: 900px) {
  .branch-home-intro {
    flex-direction: column;
  }

  .home-overview {
    min-height: min(280px, 58vw);
  }

  .featured-notice {
    flex: none;
    width: 100%;
    min-width: 0;
  }
}
</style>
